import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox, HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useIntl } from 'react-intl';

import { useDisabledDatePresets } from '@/App/tiles/shared/useDisabledDatePresets.tsx';
import { useCheckSwapByTradeCapture } from '@/App/tiles/Swap/SwapContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { useCancelSwapRfsMutation } from '@/store/swap/api/index.ts';
import { isSwapPredeliver, isSwapRollover } from '@/store/swap/helpers/tile.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';

export type SwapMainFieldsProps = {
  tileId: string;
};

export const SwapMainFields = (props: SwapMainFieldsProps) => {
  const { tileId } = props;

  const { formatMessage } = useIntl();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const [requestSwapRfsCancelation] = useCancelSwapRfsMutation();

  const {
    currencyPair,
    isNonDeliverable,
    nearNotional,
    nearNotionalCurrencyIndex,
    nearNotionalCurrency,
    nearExpiryDate,
    nearExpiryDateTenor,
    farNotional,
    farNotionalCurrencyIndex,
    farNotionalCurrency,
    farExpiryDate,
    farExpiryDateTenor,
    isUneven,
    isHistoricalRate,
    isRollover,
    isPredeliver,
    rfsId,
    errors,
  } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        isNonDeliverable: false,
        isFarNotionalFirstCurrency: true,
        isUneven: false,
        isHistoricalRate: false,
      };
    }

    return {
      currencyPair: tile.properties.currencyPair,
      isNonDeliverable: tile.isNonDeliverable ?? false,

      nearNotional: tile?.properties.nearNotional,
      nearNotionalCurrencyIndex: tile?.properties.nearNotionalCurrencyIndex,

      nearNotionalCurrency: isDefined(tile?.properties.nearNotionalCurrencyIndex)
        ? tile.properties.nearNotionalCurrencyIndex === 1
          ? tile.properties.currency1
          : tile.properties.currency2
        : undefined,

      nearExpiryDate: tile?.properties.nearExpiryDate ?? '', // return empty string if undefined to avoid uncontrolled input warning
      nearExpiryDateTenor: tile?.properties.nearExpiryDateTenor,

      farNotional: tile?.properties.farNotional,
      farNotionalCurrencyIndex: tile?.properties.farNotionalCurrencyIndex,

      farNotionalCurrency: isDefined(tile?.properties.farNotionalCurrencyIndex)
        ? tile.properties.farNotionalCurrencyIndex === 1
          ? tile.properties.currency1
          : tile.properties.currency2
        : undefined,

      farExpiryDate: tile?.properties.farExpiryDate ?? '', // return empty string if undefined to avoid uncontrolled input warning
      farExpiryDateTenor: tile?.properties.farExpiryDateTenor,

      isUneven: tile.isNonDeliverable ? false : tile.properties.type === 'uneven',

      isHistoricalRate: tile.isNonDeliverable ? false : tile.properties.type === 'historical',
      isRollover: isSwapRollover(tile),
      isPredeliver: isSwapPredeliver(tile),

      rfsId: tile.properties.rfs?.rfsId,
      errors: isDefined(tile.tradeCapture) && 'errors' in tile.tradeCapture ? tile.tradeCapture.errors : undefined,
    };
  }, deepEqual);

  const disabledDatePresets = useDisabledDatePresets(tileId, currencyPair);

  const checkSwapWithTradeCapture = useCheckSwapByTradeCapture();

  const handleNearNotionalChange = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Near notional changed', String(value));

    checkSwapWithTradeCapture({
      nearNotional: value,
      nearNotionalCurrencyIndex,
    });
  };

  const handleToggleNearNotionalCurrency = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Near notional currency changed', nearNotionalCurrencyIndex === 1 ? '2' : '1');

    checkSwapWithTradeCapture({
      nearNotional: nearNotional,
      nearNotionalCurrencyIndex: nearNotionalCurrencyIndex === 1 ? 2 : 1,
    });
  };

  const handleNearExpiryDateChange = (dateOrTenor: string) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Near expiry changed', dateOrTenor);

    checkSwapWithTradeCapture({
      nearExpiryDate: dateOrTenor,
    });
  };

  const handleFarNotionalChange = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Far notional changed', String(value));

    checkSwapWithTradeCapture({
      farNotional: value,
      farNotionalCurrencyIndex,
    });
  };

  const handleToggleFarNotionalCurrency = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Far notional currency changed', farNotionalCurrencyIndex === 1 ? '2' : '1');

    checkSwapWithTradeCapture({
      farNotional: farNotional,
      farNotionalCurrencyIndex: farNotionalCurrencyIndex === 1 ? 2 : 1,
    });
  };

  const handleFarExpiryDateChange = (dateOrTenor: string) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Far expiry changed', dateOrTenor);

    checkSwapWithTradeCapture({
      farExpiryDate: dateOrTenor,
    });
  };

  const handleToggleUneven = () => {
    if (isDefined(rfsId)) {
      requestSwapRfsCancelation({ tileId, rfsId });
    }

    sgwtWebAnalytics?.trackEvent('Swap', 'Uneven toggled', isUneven ? 'standard' : 'uneven');

    checkSwapWithTradeCapture({
      type: isUneven ? 'standard' : 'uneven',
    });
  };

  const handleToggleHistoricalRate = () => {
    if (isDefined(rfsId)) {
      requestSwapRfsCancelation({ tileId, rfsId });
    }

    sgwtWebAnalytics?.trackEvent('Swap', 'Historical toggled', isHistoricalRate ? 'standard' : 'historical');

    checkSwapWithTradeCapture({
      type: isHistoricalRate ? 'standard' : 'historical',
    });
  };

  return (
    <div className="input-group input-group-sm p-2">
      {!isNonDeliverable && (
        <HStack fullWidth horizontalAlignment="start" verticalAlignment="center" style={{ marginLeft: '-8px' }}>
          <HStack gap="8px">
            <Checkbox
              label={formatMessage({ id: 'SwapTile.uneven.label' })}
              checked={isUneven}
              onClick={handleToggleUneven}
              disabled={isHistoricalRate}
              dataE2e="uneven-checkbox"
            />
          </HStack>

          <UsertypeGuard allow="Internal Sales">
            <HStack gap="8px" verticalAlignment="center">
              <Checkbox
                label={formatMessage(
                  {
                    id: 'SwapTile.historicalRate.label',
                  },
                  { type: isRollover ? 'rollover' : isPredeliver ? 'predeliver' : '' },
                )}
                // label={`Historical rate${isRollover ? ' rollover' : isPredeliver ? ' predeliver' : ''}`}
                checked={isHistoricalRate}
                onClick={handleToggleHistoricalRate}
                disabled={isUneven}
                dataE2e="historical-rate-checkbox"
              />
            </HStack>
          </UsertypeGuard>
        </HStack>
      )}

      <div className="form-group row my-1 no-gutters">
        <div className="col-6 pe-1">
          <TradeCaptureValidationWrapper errors={errors?.nearNotional ?? []}>
            <SgNumberInput
              value={nearNotional}
              valuePosition="start"
              maxDecimals={9} // by default, the maxDecimals in Intl.NumberFormat is 3
              selectAllOnFocus
              acceleratorsEnabled
              allowNegative={false}
              placeholder={formatMessage({ id: 'SwapTile.nearNotional.placeholder' })}
              onChange={handleNearNotionalChange}
              unit={nearNotionalCurrency}
              // unitColor={nearNotionalCurrencyIndex === 1 ? 'primary' : 'primary-alt'}
              unitBackgroundColor={nearNotionalCurrencyIndex === 1 ? undefined : 'warning'}
              unitPosition="end"
              unitTabNoIndex
              onUnitClick={handleToggleNearNotionalCurrency}
              data-e2e="near-notional"
            />
          </TradeCaptureValidationWrapper>
        </div>

        <div className="col-6 ps-1">
          <TradeCaptureValidationWrapper errors={errors?.nearExpiryDate ?? []}>
            <DatePickerWithTenorNext
              date={nearExpiryDate}
              tenor={nearExpiryDateTenor as string}
              placeholder={formatMessage({ id: 'SwapTile.nearExpiry.placeholder' })}
              onChange={handleNearExpiryDateChange}
              data-e2e="near-expiry-picker"
              disablePresets={disabledDatePresets}
            />
          </TradeCaptureValidationWrapper>
        </div>
      </div>

      <div className="form-group row my-1 no-gutters">
        <div className="col-6 pe-1">
          <TradeCaptureValidationWrapper errors={errors?.farNotional ?? []}>
            <SgNumberInput
              size="sm"
              value={farNotional}
              valuePosition="start"
              maxDecimals={9} // by default, the maxDecimals in Intl.NumberFormat is 3
              selectAllOnFocus
              acceleratorsEnabled
              allowNegative={false}
              placeholder={formatMessage({ id: 'SwapTile.farNotional.placeholder' })}
              onChange={handleFarNotionalChange}
              unit={farNotionalCurrency}
              unitBackgroundColor={farNotionalCurrencyIndex === 1 ? undefined : 'warning'}
              unitPosition="end"
              unitTabNoIndex
              onUnitClick={handleToggleFarNotionalCurrency}
              data-e2e="far-notional"
            />
          </TradeCaptureValidationWrapper>
        </div>

        <div className="col-6 ps-1">
          <TradeCaptureValidationWrapper errors={errors?.farExpiryDate ?? []}>
            <DatePickerWithTenorNext
              date={farExpiryDate}
              tenor={farExpiryDateTenor as string}
              placeholder={formatMessage({ id: 'SwapTile.farExpiry.placeholder' })}
              onChange={handleFarExpiryDateChange}
              data-e2e="far-expiry-picker"
              disablePresets={disabledDatePresets}
            />
          </TradeCaptureValidationWrapper>
        </div>
      </div>
    </div>
  );
};
